import {
  ARTICLE_RESULT_FIRST_PAGE_AMOUNT,
  ARTICLE_RESULT_REST_PAGES_AMOUNT,
} from 'constants/articleResults';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React from 'react';
import {
  getArticleCategoriesInMenu,
  getPagedArticleByTag,
  getTagBySlug,
} from 'services/cmsService';
import { IArticleResult } from 'types/pages';
import { tryToNoticeError } from 'utils/errorInfo';
import { parsePageQueryParam } from 'utils/parsePageQueryParam';

import Typography from '@material-ui/core/Typography';

import ArticleListSection from 'components/CMS/ArticleListSection';
import ArticlePagination from 'components/CMS/ArticlePagination';
import { ArticleResultLanding } from 'components/CMS/ArticleResultLanding';

import { ContentHubContainer } from 'modules/ContentHubContainer';

import styles from './style.module.scss';

const ArticlesByTag = (props: IArticleResult) => {
  const router = useRouter();

  const {
    articles,
    categoriesOnMenu,
    title,
    query,
    pageCount,
    promotion,
    metaTitle,
    metaDescription,
  } = props;

  return (
    <ContentHubContainer categories={categoriesOnMenu}>
      <NextSeo
        title={metaTitle ?? undefined}
        description={metaDescription ?? undefined}
        noindex={true}
        nofollow={true}
        openGraph={{
          title: metaTitle ?? undefined,
          description: metaDescription ?? undefined,
          type: 'article',
        }}
      />

      <Typography component="h1" variant="h4" color="secondary" classes={{ h4: styles.header }}>
        #{title}
      </Typography>

      {query.page === 1 && <ArticleResultLanding articles={articles} promotion={promotion} />}
      {query.page > 1 && <ArticleListSection articles={articles} />}
      <ArticlePagination
        pageCount={pageCount}
        page={+query.page || 1}
        onChange={page => {
          router.push({ pathname: location.pathname, query: { ...query, page } });
        }}
      />
    </ContentHubContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<IArticleResult>> {
  try {
    const { slug } = ctx.query;
    const currentPage = parsePageQueryParam(ctx.query.page);

    if (slug === undefined || currentPage === null) {
      return {
        notFound: true,
      };
    }

    const query = { page: currentPage };
    const isFirstPage = currentPage === 1;
    const start = isFirstPage
      ? 0
      : ARTICLE_RESULT_FIRST_PAGE_AMOUNT + (currentPage - 2) * ARTICLE_RESULT_REST_PAGES_AMOUNT;

    const [{ articles, total }, categories, tag] = await Promise.all([
      getPagedArticleByTag(
        slug as string,
        isFirstPage ? ARTICLE_RESULT_FIRST_PAGE_AMOUNT : ARTICLE_RESULT_REST_PAGES_AMOUNT,
        start,
      ),
      getArticleCategoriesInMenu(),
      getTagBySlug(slug),
    ]);
    if (!tag) {
      return {
        notFound: true,
      };
    }

    const totalWithoutFirstPage = total - ARTICLE_RESULT_FIRST_PAGE_AMOUNT;
    const pageCount = Math.ceil(totalWithoutFirstPage / ARTICLE_RESULT_REST_PAGES_AMOUNT) + 1;

    return {
      props: {
        slug: slug as string,
        query,
        pageCount,
        articles: articles ?? [],
        categoriesOnMenu: categories,
        title: tag.name,
        promotion: tag.article_promotion,
        metaTitle: tag.meta_title,
        metaDescription: tag.meta_description,
      },
    };
  } catch (error) {
    const errorMessage = `Article Tag Data Error: ${error.message}`;
    tryToNoticeError(new Error(errorMessage, { cause: error }), { error });
    return { notFound: true };
  }
}

export default ArticlesByTag;
